/**
 * This file contains all possible ids 'owned' by us.
 * It's important to include all ids here so that we can
 * help protect against IDOR.
 *
 * Ideally it is kept in alphabetical order
 */
import { Literal, Number, Static, String, Union } from "runtypes";

import { IntrinsicGroupId } from "./enums.js";
import {
  PaymentMethodId,
  StripeCustomerId,
  StripePriceId,
  StripeProductId,
  StripeSubscriptionId,
  StripeSubscriptionItemId,
} from "./stripeIdTypeBrands.js";
import { KernelImage } from "./typeBrands.js";

export const AppSessionAOLogId = String.withBrand("AppSessionAOLogId");
export type AppSessionAOLogId = Static<typeof AppSessionAOLogId>;

export const AppSessionCellId = String.withBrand("AppSessionCellId");
export type AppSessionCellId = Static<typeof AppSessionCellId>;

export const AppSessionEventLogId = String.withBrand("AppSessionEventLogId");
export type AppSessionEventLogId = Static<typeof AppSessionEventLogId>;

export const AppSessionGrantId = String.withBrand("AppSessionGrantId");
export type AppSessionGrantId = Static<typeof AppSessionGrantId>;

export const AppSessionId = String.withBrand("AppSessionId");
export type AppSessionId = Static<typeof AppSessionId>;

export const AppSessionDeletionEventId = String.withBrand(
  "AppSessionDeletionEventId",
);
export type AppSessionDeletionEventId = Static<
  typeof AppSessionDeletionEventId
>;

export const SavedViewId = String.withBrand("SavedViewId");
export type SavedViewId = Static<typeof SavedViewId>;

export const ApiClientId = String.withBrand("ApiClientId");
export type ApiClientId = Static<typeof ApiClientId>;

export const BigQueryOAuthConnectionId = String.withBrand(
  "BigQueryOAuthConnectionId",
);
export type BigQueryOAuthConnectionId = Static<
  typeof BigQueryOAuthConnectionId
>;

export const BlockCellId = String.withBrand("BlockCellId");
export type BlockCellId = Static<typeof BlockCellId>;

export const CanvasElementId = String.withBrand("CanvasElementId");
export type CanvasElementId = Static<typeof CanvasElementId>;

export const DataAppElementId = String.withBrand("DataAppElementId");
export type DataAppElementId = Static<typeof DataAppElementId>;

export const CanvasLayoutId = String.withBrand("CanvasLayoutId");
export type CanvasLayoutId = Static<typeof CanvasLayoutId>;

export const CategoryId = String.withBrand("CategoryId");
export type CategoryId = Static<typeof CategoryId>;

export const CellGroupCellId = String.withBrand("CellGroupCellId");
export type CellGroupCellId = Static<typeof CellGroupCellId>;

export const CellId = String.withBrand("CellId");
export type CellId = Static<typeof CellId>;

export const ChartCellId = String.withBrand("ChartCellId");
export type ChartCellId = Static<typeof ChartCellId>;

export const ChartCellSessionStateId = String.withBrand(
  "ChartCellSessionStateId",
);
export type ChartCellSessionStateId = Static<typeof ChartCellSessionStateId>;

export const CompactedAppSessionEventLogId = String.withBrand(
  "CompactedAppSessionEventLogId",
);
export type CompactedAppSessionEventLogId = Static<
  typeof CompactedAppSessionEventLogId
>;

export const ExploreCellId = String.withBrand("ExploreCellId");
export type ExploreCellId = Static<typeof ExploreCellId>;

export const CodeCellId = String.withBrand("CodeCellId");
export type CodeCellId = Static<typeof CodeCellId>;

export const CollectionGrantId = String.withBrand("CollectionGrantId");
export type CollectionGrantId = Static<typeof CollectionGrantId>;

export const CollectionHexLinkId = String.withBrand("CollectionHexLinkId");
export type CollectionHexLinkId = Static<typeof CollectionHexLinkId>;

export const CollectionId = String.withBrand("CollectionId");
export type CollectionId = Static<typeof CollectionId>;

export const ColumnPropertiesId = String.withBrand("ColumnPropertiesId");
export type ColumnPropertiesId = Static<typeof ColumnPropertiesId>;

export const ColorPaletteId = String.withBrand("ColorPaletteId");
export type ColorPaletteId = Static<typeof ColorPaletteId>;

export const CommentId = String.withBrand("CommentId");
export type CommentId = Static<typeof CommentId>;

export const CommentReactionId = String.withBrand("CommentReactionId");
export type CommentReactionId = Static<typeof CommentReactionId>;

export const ComponentImportCellId = String.withBrand("ComponentImportCellId");
export type ComponentImportCellId = Static<typeof ComponentImportCellId>;

export const ComputeActionId = String.withBrand("ComputeActionId");
export type ComputeActionId = Static<typeof ComputeActionId>;

export const ComputeActionBatchId = String.withBrand("ComputeActionBatchId");
/**
 * A non-entity id used to batch compute actions that were queued at the same time together
 */
export type ComputeActionBatchId = Static<typeof ComputeActionBatchId>;

export const DataConnectionHexVersionLinkId = String.withBrand(
  "DataConnectionHexVersionLinkId",
);
export type DataConnectionHexVersionLinkId = Static<
  typeof DataConnectionHexVersionLinkId
>;

export const DataConnectionId = String.withBrand("DataConnectionId");
export type DataConnectionId = Static<typeof DataConnectionId>;

export const DataConnectionSchemaRefreshId = String.withBrand(
  "DataConnectionSchemaRefreshId",
);
export type DataConnectionSchemaRefreshId = Static<
  typeof DataConnectionSchemaRefreshId
>;

export const DataConnectionSchemaRefreshScheduleId = String.withBrand(
  "DataConnectionSchemaRefreshScheduleId",
);
export type DataConnectionSchemaRefreshScheduleId = Static<
  typeof DataConnectionSchemaRefreshScheduleId
>;

export const DataConnectionOAuthSettingsId = String.withBrand(
  "DataConnectionOAuthSettingsId",
);
export type DataConnectionOAuthSettingsId = Static<
  typeof DataConnectionOAuthSettingsId
>;

export const DataSourceGrantId = String.withBrand("DataSourceGrantId");
export type DataSourceGrantId = Static<typeof DataSourceGrantId>;

export const DataSourceGrantStableId = String.withBrand(
  "DataSourceGrantStableId",
);
export type DataSourceGrantStableId = Static<typeof DataSourceGrantStableId>;

export const SchemaRefreshEntityDiffStableId = String.withBrand(
  "SchemaRefreshEntityDiffStableId",
);
export type SchemaRefreshEntityDiffStableId = Static<
  typeof SchemaRefreshEntityDiffStableId
>;

export const DataSourceColumnId = String.withBrand("DataSourceColumnId");
export type DataSourceColumnId = Static<typeof DataSourceColumnId>;

export const DataSourceColumnMetadataId = String.withBrand(
  "DataSourceColumnMetadataId",
);
export type DataSourceColumnMetadataId = Static<
  typeof DataSourceColumnMetadataId
>;

export const DataSourceDatabaseId = String.withBrand("DataSourceDatabaseId");
export type DataSourceDatabaseId = Static<typeof DataSourceDatabaseId>;

export const DataSourceDatabaseMetadataId = String.withBrand(
  "DataSourceDatabaseMetadataId",
);
export type DataSourceDatabaseMetadataId = Static<
  typeof DataSourceDatabaseMetadataId
>;

export const DataSourceDbtMetricId = String.withBrand("DataSourceDbtMetricId");
export type DataSourceDbtMetricId = Static<typeof DataSourceDbtMetricId>;

export const DataSourceSchemaId = String.withBrand("DataSourceSchemaId");
export type DataSourceSchemaId = Static<typeof DataSourceSchemaId>;

export const DataSourceSchemaMetadataId = String.withBrand(
  "DataSourceSchemaMetadataId",
);
export type DataSourceSchemaMetadataId = Static<
  typeof DataSourceSchemaMetadataId
>;

export const DataSourceTableId = String.withBrand("DataSourceTableId");
export type DataSourceTableId = Static<typeof DataSourceTableId>;

export const DataSourceTableMetadataId = String.withBrand(
  "DataSourceTableMetadataId",
);
export type DataSourceTableMetadataId = Static<
  typeof DataSourceTableMetadataId
>;

export type DataSourceEntityId =
  | DataSourceColumnId
  | DataSourceTableId
  | DataSourceSchemaId
  | DataSourceDatabaseId;

export const DbtMetricCellDataSourceDbtMetricLinkId = String.withBrand(
  "DbtMetricCellDataSourceDbtMetricLinkId",
);
export type DbtMetricCellDataSourceDbtMetricLinkId = Static<
  typeof DbtMetricCellDataSourceDbtMetricLinkId
>;

export const DatabricksOAuthConnectionId = String.withBrand(
  "DatabricksOAuthConnectionId",
);
export type DatabricksOAuthConnectionId = Static<
  typeof DatabricksOAuthConnectionId
>;

export const DataSourceObjectCategoryLinkId = String.withBrand(
  "DataSourceObjectCategoryLinkId",
);
export type DataSourceObjectCategoryLinkId = Static<
  typeof DataSourceObjectCategoryLinkId
>;

export const DbtMetricCellId = String.withBrand("DbtMetricCellId");
export type DbtMetricCellId = Static<typeof DbtMetricCellId>;

export const DeviceCodeId = String.withBrand("DeviceCodeId");
export type DeviceCodeId = Static<typeof DeviceCodeId>;

export const DisplayTableCellId = String.withBrand("DisplayTableCellId");
export type DisplayTableCellId = Static<typeof DisplayTableCellId>;

export const DisplayTableColumnId = String.withBrand("DisplayTableColumnId");
export type DisplayTableColumnId = Static<typeof DisplayTableColumnId>;

export const DisplayTableConfigId = String.withBrand("DisplayTableConfigId");
export type DisplayTableConfigId = Static<typeof DisplayTableConfigId>;

export const DisplayTableSessionStateId = String.withBrand(
  "DisplayTableSessionStateId",
);
export type DisplayTableSessionStateId = Static<
  typeof DisplayTableSessionStateId
>;

export const DomainDenialId = String.withBrand("DomainDenialId");
export type DomainDenialId = Static<typeof DomainDenialId>;

export const EmailDenialId = String.withBrand("EmailDenialId");
export type EmailDenialId = Static<typeof EmailDenialId>;

export const EvalBaseId = String.withBrand("EvalBaseId");
export type EvalBaseId = Static<typeof EvalBaseId>;

export const EvalResultId = String.withBrand("EvalResultId");
export type EvalResultId = Static<typeof EvalResultId>;

export const EvalRubricId = String.withBrand("EvalRubricId");
export type EvalRubricId = Static<typeof EvalRubricId>;

export const EvalRunId = String.withBrand("EvalRunId");
export type EvalRunId = Static<typeof EvalRunId>;

export const EvalSetId = String.withBrand("EvalSetId");
export type EvalSetId = Static<typeof EvalSetId>;

export const EvalSetRunId = String.withBrand("EvalSetRunId");
export type EvalSetRunId = Static<typeof EvalSetRunId>;

export const EvalSemanticProjectFileId = String.withBrand(
  "EvalSemanticProjectFileId",
);
export type EvalSemanticProjectFileId = Static<
  typeof EvalSemanticProjectFileId
>;

export const EmbeddedApiCallId = String.withBrand("EmbeddedApiCallId");
export type EmbeddedApiCallId = Static<typeof EmbeddedApiCallId>;

export const EmbeddedOAuthCredentialsId = String.withBrand(
  "EmbeddedOAuthCredentialsId",
);
export type EmbeddedOAuthCredentialsId = Static<
  typeof EmbeddedOAuthCredentialsId
>;

export const EnvVarId = String.withBrand("EnvVarId");
export type EnvVarId = Static<typeof EnvVarId>;

export const ESDocId = String.withBrand("ESDocId");
export type ESDocId = Static<typeof ESDocId>;

export const ExternalContextConnectionId = String.withBrand(
  "ExternalContextConnectionId",
);
export type ExternalContextConnectionId = Static<
  typeof ExternalContextConnectionId
>;

export const ExploreAgentSettingsId = String.withBrand(
  "ExploreAgentSettingsId",
);
export type ExploreAgentSettingsId = Static<typeof ExploreAgentSettingsId>;

export const ExploreSpecSessionStateId = String.withBrand(
  "ExploreSpecSessionStateId",
);
export type ExploreSpecSessionStateId = Static<
  typeof ExploreSpecSessionStateId
>;

export const ExternalFileIntegrationId = String.withBrand(
  "ExternalFileIntegrationId",
);
export type ExternalFileIntegrationId = Static<
  typeof ExternalFileIntegrationId
>;

export const ExternalFileIntegrationHexVersionLinkId = String.withBrand(
  "ExternalFileIntegrationHexVersionLinkId",
);
export type ExternalFileIntegrationHexVersionLinkId = Static<
  typeof ExternalFileIntegrationHexVersionLinkId
>;

export const GoogleDriveOauthAccountId = String.withBrand(
  "GoogleDriveOauthAccountId",
);
export type GoogleDriveOauthAccountId = Static<
  typeof GoogleDriveOauthAccountId
>;

export const FileHexVersionLinkId = String.withBrand("FileHexVersionLinkId");
export type FileHexVersionLinkId = Static<typeof FileHexVersionLinkId>;

export const FileId = String.withBrand("FileId");
export type FileId = Static<typeof FileId>;

export const AppSessionFileId = String.withBrand("AppSessionFileId");
export type AppSessionFileId = Static<typeof AppSessionFileId>;

export type BaseFileId = FileId | AppSessionFileId;

export const FilterCellColumnId = String.withBrand("FilterCellColumn");
export type FilterCellColumnId = Static<typeof FilterCellColumnId>;

export const FilterCellId = String.withBrand("FilterCellId");
export type FilterCellId = Static<typeof FilterCellId>;

export const FilterCellSessionStateId = String.withBrand(
  "FilterCellSessionStateId",
);
export type FilterCellSessionStateId = Static<typeof FilterCellSessionStateId>;

export const GridElementId = String.withBrand("GridElementId");
export type GridElementId = Static<typeof GridElementId>;

export const GridColumnId = String.withBrand("GridColumnId");
export type GridColumnId = Static<typeof GridColumnId>;

export const GridLayoutId = String.withBrand("GridLayoutId");
export type GridLayoutId = Static<typeof GridLayoutId>;

export const GridRowId = String.withBrand("GridRowId");
export type GridRowId = Static<typeof GridRowId>;

export const StaticGridLayoutId = String.withBrand("StaticGridLayoutId");
export type StaticGridLayoutId = Static<typeof StaticGridLayoutId>;

export const GroupId = String.withBrand("GroupId");
export type GroupId = Static<typeof GroupId>;

export const GithubServerConfigId = String.withBrand("GithubServerConfigId");
export type GithubServerConfigId = Static<typeof GithubServerConfigId>;

export const HexAnalyticsId = String.withBrand("HexAnalyticsId");
export type HexAnalyticsId = Static<typeof HexAnalyticsId>;

export const HexId = String.withBrand("HexId");
export type HexId = Static<typeof HexId>;

export const HexVersionOAuthSettingsId = String.withBrand(
  "HexVersionOAuthSettingsId",
);
export type HexVersionOAuthSettingsId = Static<
  typeof HexVersionOAuthSettingsId
>;

export const HexScheduleId = String.withBrand("HexScheduleId");
export type HexScheduleId = Static<typeof HexScheduleId>;

export const ThreadExtractionEvalResultId = String.withBrand(
  "ThreadExtractionEvalResultId",
);
export type ThreadExtractionEvalResultId = Static<
  typeof ThreadExtractionEvalResultId
>;

export const ThreadExtractionExperimentId = String.withBrand(
  "ThreadExtractionExperimentId",
);
export type ThreadExtractionExperimentId = Static<
  typeof ThreadExtractionExperimentId
>;
export const ScheduleNotificationId = String.withBrand(
  "ScheduleNotificationId",
);
export type ScheduleNotificationId = Static<typeof ScheduleNotificationId>;

export const ScheduleNotificationExportId = String.withBrand(
  "ScheduleNotificationExportId",
);
export type ScheduleNotificationExportId = Static<
  typeof ScheduleNotificationExportId
>;

export const SavedViewHexScheduleLinkId = String.withBrand(
  "SavedViewHexScheduleLinkId",
);
export type SavedViewHexScheduleLinkId = Static<
  typeof SavedViewHexScheduleLinkId
>;

export const SavedViewScheduleNotificationLinkId = String.withBrand(
  "SavedViewScheduleNotificationLinkId",
);
export type SavedViewScheduleNotificationLinkId = Static<
  typeof SavedViewScheduleNotificationLinkId
>;

export const ScheduleNotificationConditionId = String.withBrand(
  "ScheduleNotificationConditionId",
);
export type ScheduleNotificationConditionId = Static<
  typeof ScheduleNotificationConditionId
>;

export const RunNotificationRecipientId = String.withBrand(
  "RunNotificationRecipientId",
);
export type RunNotificationRecipientId = Static<
  typeof RunNotificationRecipientId
>;

export const SlackChannelId = String.withBrand("SlackChannelId");
export type SlackChannelId = Static<typeof SlackChannelId>;

export const SlackTeamAuthorizationId = String.withBrand(
  "SlackTeamAuthorizationId",
);
export type SlackTeamAuthorizationId = Static<typeof SlackTeamAuthorizationId>;

export const SlackUserId = String.withBrand("SlackUserId");
export type SlackUserId = Static<typeof SlackUserId>;

export const SlackAgentChatThreadLinkId = String.withBrand(
  "SlackAgentChatThreadLinkId",
);
export type SlackAgentChatThreadLinkId = Static<
  typeof SlackAgentChatThreadLinkId
>;

export const HexAOLogId = String.withBrand("HexAOLogId");
export type HexAOLogId = Static<typeof HexAOLogId>;

export const HexVersionAOLogId = String.withBrand("HexVersionAOLogId");
export type HexVersionAOLogId = Static<typeof HexVersionAOLogId>;

export const HexCategoryLinkId = String.withBrand("HexCategoryLinkId");
export type HexCategoryLinkId = Static<typeof HexCategoryLinkId>;

export const HexVersionId = String.withBrand("HexVersionId");
export type HexVersionId = Static<typeof HexVersionId>;

export const HexVersionDefaultAppSessionId = String.withBrand(
  "HexVersionDefaultAppSessionId",
);
export type HexVersionDefaultAppSessionId = Static<
  typeof HexVersionDefaultAppSessionId
>;

export const HexVersionSecretId = String.withBrand("HexVersionSecretId");
export type HexVersionSecretId = Static<typeof HexVersionSecretId>;

export const DockerImageRepositoryId = String.withBrand(
  "DockerImageRepositoryId",
);
export type DockerImageRepositoryId = Static<typeof DockerImageRepositoryId>;

export const DockerImageTagId = String.withBrand("DockerImageTagId");
export type DockerImageTagId = Static<typeof DockerImageTagId>;

export const ComputeHeartbeatId = String.withBrand("ComputeHeartbeatId");
export type ComputeHeartbeatId = Static<typeof ComputeHeartbeatId>;

export const KernelId = String.withBrand("KernelId");
export type KernelId = Static<typeof KernelId>;

export const KernelSourceImageId = String.withBrand("KernelSourceImageId");
export type KernelSourceImageId = Static<typeof KernelSourceImageId>;

export const KernelSourceImageVersionId = String.withBrand(
  "KernelSourceImageVersionId",
);
export type KernelSourceImageVersionId = Static<
  typeof KernelSourceImageVersionId
>;

export const LinkId = String.withBrand("LinkId");
export type LinkId = Static<typeof LinkId>;

export const MagicApiKeyId = String.withBrand("MagicApiKeyId");
export type MagicApiKeyId = Static<typeof MagicApiKeyId>;

export const MagicRulesFileId = String.withBrand("MagicRulesFileId");
export type MagicRulesFileId = Static<typeof MagicRulesFileId>;

export const MagicEventId = String.withBrand("MagicEventId");
export type MagicEventId = Static<typeof MagicEventId>;

export const SystemMagicEventId = String.withBrand("SystemMagicEventId");
export type SystemMagicEventId = Static<typeof SystemMagicEventId>;

export const MagicCompletionEventId = String.withBrand(
  "MagicCompletionEventId",
);
export type MagicCompletionEventId = Static<typeof MagicCompletionEventId>;

export const LlmRequestLogId = String.withBrand("LlmRequestLogId");
export type LlmRequestLogId = Static<typeof LlmRequestLogId>;

export const MagicCompletionContextId = String.withBrand(
  "MagicCompletionContextId",
);
export type MagicCompletionContextId = Static<typeof MagicCompletionContextId>;

export const MagicCellContentsId = String.withBrand("MagicCellContentsId");
export type MagicCellContentsId = Static<typeof MagicCellContentsId>;

export const MapCellId = String.withBrand("MapCellId");
export type MapCellId = Static<typeof MapCellId>;

export const MapLayerId = String.withBrand("MapLayer");
export type MapLayerId = Static<typeof MapLayerId>;

export const MarkdownCellId = String.withBrand("MarkdownCellId");
export type MarkdownCellId = Static<typeof MarkdownCellId>;

export const McpAppSwapTokenId = String.withBrand("McpAppSwapTokenId");
export type McpAppSwapTokenId = Static<typeof McpAppSwapTokenId>;

export const MetricCellId = String.withBrand("MetricCellId");
export type MetricCellId = Static<typeof MetricCellId>;

export const NotificationPreferencesId = String.withBrand(
  "NotificationPreferencesId",
);
export type NotificationPreferencesId = Static<
  typeof NotificationPreferencesId
>;

export const NotionBotId = String.withBrand("NotionBotId");
export type NotionBotId = Static<typeof NotionBotId>;

export const NotionPreviewId = String.withBrand("NotionPreviewId");
export type NotionPreviewId = Static<typeof NotionPreviewId>;

export const OrgAppIconId = String.withBrand("OrgAppIconId");
export type OrgAppIconId = Static<typeof OrgAppIconId>;

export const OrgDomainId = String.withBrand("OrgDomainId");
export type OrgDomainId = Static<typeof OrgDomainId>;

export const OrgDomainVerificationLinkId = String.withBrand(
  "OrgDomainVerificationLinkId",
);
export type OrgDomainVerificationLinkId = Static<
  typeof OrgDomainVerificationLinkId
>;

export const OrgId = String.withBrand("OrgId");
export type OrgId = Static<typeof OrgId>;

export const McpServerConfigurationId = String.withBrand(
  "McpServerConfigurationId",
);
export type McpServerConfigurationId = Static<typeof McpServerConfigurationId>;

export const OrgCustomFontId = String.withBrand("OrgCustomFontId");
export type OrgCustomFontId = Static<typeof OrgCustomFontId>;

export const OrgPublishedAppThemeId = String.withBrand(
  "OrgPublishedAppThemeId",
);
export type OrgPublishedAppThemeId = Static<typeof OrgPublishedAppThemeId>;

export const OrgGuideFileId = String.withBrand("OrgGuideFileId");
export type OrgGuideFileId = Static<typeof OrgGuideFileId>;

export const OrgGuideFileVersionId = String.withBrand("OrgGuideFileVersionId");
export type OrgGuideFileVersionId = Static<typeof OrgGuideFileVersionId>;

export const OrgGuideFileVersionMentionId = String.withBrand(
  "OrgGuideFileVersionMentionId",
);
export type OrgGuideFileVersionMentionId = Static<
  typeof OrgGuideFileVersionMentionId
>;

export const OrgGuideFilesAOLogId = String.withBrand("OrgGuideFilesAOLogId");
export type OrgGuideFilesAOLogId = Static<typeof OrgGuideFilesAOLogId>;

export const OrgGuideFilePath = String.withBrand("OrgGuideFilePath");
export type OrgGuideFilePath = Static<typeof OrgGuideFilePath>;

export const OutputContentId = String.withBrand("OutputContentId");
export type OutputContentId = Static<typeof OutputContentId>;

export const OutputContentKeyId = String.withBrand("OutputContentKeyId");
export type OutputContentKeyId = Static<typeof OutputContentKeyId>;

export const OutputContentParts = Number.withBrand("OutputContentParts");
export type OutputContentParts = Static<typeof OutputContentParts>;

export const OutputContentPartNo = Number.withBrand("OutputContentPartNo");
export type OutputContentPartNo = Static<typeof OutputContentPartNo>;

export const OutputId = String.withBrand("OutputId");
export type OutputId = Static<typeof OutputId>;

export const PackageId = String.withBrand("PackageId");
export type PackageId = Static<typeof PackageId>;

export const ParameterValueId = String.withBrand("ParameterValueId");
export type ParameterValueId = Static<typeof ParameterValueId>;

export const ParameterId = String.withBrand("ParameterId");
export type ParameterId = Static<typeof ParameterId>;

export const PrivilegeGrantee = String.withBrand("PrivilegeGrantee");
export type PrivilegeGrantee = Static<typeof PrivilegeGrantee>;

export const ParentDataConnectionId = String.withBrand(
  "ParentDataConnectionId",
);
export type ParentDataConnectionId = Static<typeof ParentDataConnectionId>;

export const PinnedSchemaObjectId = String.withBrand("PinnedSchemaObjectId");
export type PinnedSchemaObjectId = Static<typeof PinnedSchemaObjectId>;

export const PivotCellId = String.withBrand("PivotCellId");
export type PivotCellId = Static<typeof PivotCellId>;

export const PresenceId = String.withBrand("PresenceId");
export type PresenceId = Static<typeof PresenceId>;

export const PresignedEmbeddingUrlId = String.withBrand(
  "PresignedEmbeddingUrlId",
);
export type PresignedEmbeddingUrlId = Static<typeof PresignedEmbeddingUrlId>;

export const PaidComputeGrantId = String.withBrand("PaidComputeGrantId");
export type PaidComputeGrantId = Static<typeof PaidComputeGrantId>;

export const ProjectGrantId = String.withBrand("ProjectGrantId");
export type ProjectGrantId = Static<typeof ProjectGrantId>;

export const HexRetrievalEventId = String.withBrand("HexRetrievalEventId");
export type HexRetrievalEventId = Static<typeof HexRetrievalEventId>;

export const ProjectViewId = String.withBrand("ProjectViewId");
export type ProjectViewId = Static<typeof ProjectViewId>;

export const PromoId = String.withBrand("PromoId");
export type PromoId = Static<typeof PromoId>;

export const ExploreId = String.withBrand("ExploreId");
export type ExploreId = Static<typeof ExploreId>;

export const ExploreSessionStateId = String.withBrand("ExploreSessionStateId");
export type ExploreSessionStateId = Static<typeof ExploreSessionStateId>;

export const ReviewId = String.withBrand("ReviewId");
export type ReviewId = Static<typeof ReviewId>;

export const ReviewRequestId = String.withBrand("ReviewRequestId");
export type ReviewRequestId = Static<typeof ReviewRequestId>;

export const ReviewRequestLinkId = String.withBrand("ReviewRequestLinkId");
export type ReviewRequestLinkId = Static<typeof ReviewRequestLinkId>;

export const ReviewRequestAOLogId = String.withBrand("ReviewRequestAOLogId");
export type ReviewRequestAOLogId = Static<typeof ReviewRequestAOLogId>;

export const SemanticProjectVersionAOLogId = String.withBrand(
  "SemanticProjectVersionAOLogId",
);
export type SemanticProjectVersionAOLogId = Static<
  typeof SemanticProjectVersionAOLogId
>;

export const ScheduleNotificationRecipientId = String.withBrand(
  "ScheduleNotificationRecipientId",
);
export type ScheduleNotificationRecipientId = Static<
  typeof ScheduleNotificationRecipientId
>;

export const ScopeItemId = String.withBrand("ScopeItemId");
export type ScopeItemId = Static<typeof ScopeItemId>;

export const SearchHexesResultId = String.withBrand("SearchHexesResultId");
export type SearchHexesResultId = Static<typeof SearchHexesResultId>;

export const SecretId = String.withBrand("SecretId");
export type SecretId = Static<typeof SecretId>;

export const SharedFilterId = String.withBrand("SharedFilterId");
export type SharedFilterId = Static<typeof SharedFilterId>;

export const SharedFilterSessionStateId = String.withBrand(
  "SharedFilterSessionStateId",
);
export type SharedFilterSessionStateId = Static<
  typeof SharedFilterSessionStateId
>;

export const SharedAssetGrantId = String.withBrand("SharedAssetGrantId");
export type SharedAssetGrantId = Static<typeof SharedAssetGrantId>;

export const SlackAuthorizationId = String.withBrand("SlackAuthorizationId");
export type SlackAuthorizationId = Static<typeof SlackAuthorizationId>;

export const SnowflakeOAuthConnectionId = String.withBrand(
  "SnowflakeOAuthConnectionId",
);
export type SnowflakeOAuthConnectionId = Static<
  typeof SnowflakeOAuthConnectionId
>;

export const SnowflakeOAuthRolePreferenceId = String.withBrand(
  "SnowflakeOAuthRolePreferenceId",
);
export type SnowflakeOAuthRolePreferenceId = Static<
  typeof SnowflakeOAuthRolePreferenceId
>;

export const SqlCellId = String.withBrand("SqlCellId");
export type SqlCellId = Static<typeof SqlCellId>;

export const SqlConfigId = String.withBrand("SqlConfigId");
export type SqlConfigId = Static<typeof SqlConfigId>;

export const SemanticRetrievalEventId = String.withBrand(
  "SemanticRetrievalEventId",
);
export type SemanticRetrievalEventId = Static<typeof SemanticRetrievalEventId>;

export const SshKeyId = String.withBrand("SshKeyId");
export type SshKeyId = Static<typeof SshKeyId>;

export const StaticCellId = String.withBrand("StaticCellId");
export type StaticCellId = Static<typeof StaticCellId>;

export const StaticDataConnectionId = String.withBrand(
  "StaticDataConnectionId",
);
export type StaticDataConnectionId = Static<typeof StaticDataConnectionId>;

export const StaticSharedFilterId = String.withBrand("StaticSharedFilterId");
export type StaticSharedFilterId = Static<typeof StaticSharedFilterId>;

export const StatusId = String.withBrand("StatusId");
export type StatusId = Static<typeof StatusId>;

export const StoryElementId = String.withBrand("StoryElementId");
export type StoryElementId = Static<typeof StoryElementId>;

export const StoryLayoutId = String.withBrand("StoryLayoutId");
export type StoryLayoutId = Static<typeof StoryLayoutId>;

export const SyncRepositoryHexVersionLinkId = String.withBrand(
  "SyncRepositoryHexVersionLinkId",
);
export type SyncRepositoryHexVersionLinkId = Static<
  typeof SyncRepositoryHexVersionLinkId
>;

export const SyncRepositoryId = String.withBrand("SyncRepositoryId");
export type SyncRepositoryId = Static<typeof SyncRepositoryId>;

export const TaskId = String.withBrand("TaskId");
export type TaskId = Static<typeof TaskId>;

export const TaskConfigId = String.withBrand("TaskConfigId");
export type TaskConfigId = Static<typeof TaskConfigId>;

export const TaskRecipientId = String.withBrand("TaskRecipientId");
export type TaskRecipientId = Static<typeof TaskRecipientId>;

export const TextCellId = String.withBrand("TextCellId");
export type TextCellId = Static<typeof TextCellId>;

export const UILockId = String.withBrand("UILockId");
export type UILockId = Static<typeof UILockId>;

export const UserGroupLinkId = String.withBrand("UserGroupLinkId");
export type UserGroupLinkId = Static<typeof UserGroupLinkId>;

export const UserId = String.withBrand("UserId");
export type UserId = Static<typeof UserId>;

export const UserMcpServerConnectionId = String.withBrand(
  "UserMcpServerConnectionId",
);
export type UserMcpServerConnectionId = Static<
  typeof UserMcpServerConnectionId
>;

export const ServerId = Literal("SERVER_API_CLIENT");
export type ServerId = Static<typeof ServerId>;

export const AiAgentId = String.withBrand("AiAgentId");
export type AiAgentId = Static<typeof AiAgentId>;

export const ClientId = Union(UserId, ApiClientId, ServerId, AiAgentId);
export type ClientId = Static<typeof ClientId>;

export const ExecutionEventId = String.withBrand("ExecutionEventId");
export type ExecutionEventId = Static<typeof ExecutionEventId>;

export const ProjectStateId = String.withBrand("ProjectStateId");
export type ProjectStateId = Static<typeof ProjectStateId>;

export const ProjectRunMetadataId = String.withBrand("ProjectRunMetadataId");
export type ProjectRunMetadataId = Static<typeof ProjectRunMetadataId>;

export const UserSnowflakeOAuthConnectionId = String.withBrand(
  "UserSnowflakeOAuthConnectionId",
);
export type UserSnowflakeOAuthConnectionId = Static<
  typeof UserSnowflakeOAuthConnectionId
>;

export const UserBigQueryOAuthConnectionId = String.withBrand(
  "UserBigQueryOAuthConnectionId",
);
export type UserBigQueryOAuthConnectionId = Static<
  typeof UserBigQueryOAuthConnectionId
>;

export const UserDatabricksOAuthConnectionId = String.withBrand(
  "UserDatabricksOAuthConnectionId",
);
export type UserDatabricksOAuthConnectionId = Static<
  typeof UserDatabricksOAuthConnectionId
>;

export const UserMCPApplicationId = String.withBrand("UserMCPApplicationId");
export type UserMCPApplicationId = Static<typeof UserMCPApplicationId>;

export const UserMemoryId = String.withBrand("UserMemoryId");
export type UserMemoryId = Static<typeof UserMemoryId>;
export const UserMemoryVersionId = String.withBrand("UserMemoryVersionId");
export type UserMemoryVersionId = Static<typeof UserMemoryVersionId>;
export const UserMemoryExtractionRunId = String.withBrand(
  "UserMemoryExtractionRunId",
);
export type UserMemoryExtractionRunId = Static<
  typeof UserMemoryExtractionRunId
>;

export const UserStarredHexLinkId = String.withBrand("UserStarredHexLinkId");
export type UserStarredHexLinkId = Static<typeof UserStarredHexLinkId>;

export const UserStarredCollectionLinkId = String.withBrand(
  "UserStarredCollectionLinkId",
);
export type UserStarredCollectionLinkId = Static<
  typeof UserStarredCollectionLinkId
>;

export const VcsAccessTokenId = String.withBrand("VcsAccessTokenId");
export type VcsAccessTokenId = Static<typeof VcsAccessTokenId>;

export const VcsConnectionId = String.withBrand("VcsConnectionId");
export type VcsConnectionId = Static<typeof VcsConnectionId>;

export const VcsPackageId = String.withBrand("VcsPackageId");
export type VcsPackageId = Static<typeof VcsPackageId>;

export const VcsPackageHexVersionLinkId = String.withBrand(
  "VcsPackageHexVersionLinkId",
);
export type VcsPackageHexVersionLinkId = Static<
  typeof VcsPackageHexVersionLinkId
>;

export const VegaChartCellId = String.withBrand("VegaChartCellId");
export type VegaChartCellId = Static<typeof VegaChartCellId>;

export const WritebackCellId = String.withBrand("WritebackCellId");
export type WritebackCellId = Static<typeof WritebackCellId>;

export const CustomCodeFormattingConfigId = String.withBrand(
  "CustomCodeFormattingConfigId",
);
export type CustomCodeFormattingConfigId = Static<
  typeof CustomCodeFormattingConfigId
>;

export const CustomSuggestionsConfigId = String.withBrand(
  "CustomSuggestionsConfigId",
);
export type CustomSuggestionsConfigId = Static<
  typeof CustomSuggestionsConfigId
>;

export const TableQueryEventId = String.withBrand("TableQueryEventId");
export type TableQueryEventId = Static<typeof TableQueryEventId>;

export const TableCoocQueryEventId = String.withBrand("TableCoocQueryEventId");
export type TableCoocQueryEventId = Static<typeof TableCoocQueryEventId>;

export const LanceTableOfDataSourceColumnsId = String.withBrand(
  "LanceTableOfDataSourceColumnsId",
);
export type LanceTableOfDataSourceColumnsId = Static<
  typeof LanceTableOfDataSourceColumnsId
>;

export const LanceTableOfHexesId = String.withBrand("LanceTableOfHexesId");
export type LanceTableOfHexesId = Static<typeof LanceTableOfHexesId>;

export const LanceTableReferenceId = String.withBrand("LanceTableReferenceId");
export type LanceTableReferenceId = Static<typeof LanceTableReferenceId>;

export const LanceTableReferenceConstraintId = String.withBrand(
  "LanceTableReferenceConstraintId",
);
export type LanceTableReferenceConstraintId = Static<
  typeof LanceTableReferenceConstraintId
>;

export const SchemaRefreshRequestId = String.withBrand(
  "SchemaRefreshRequestId",
);
export type SchemaRefreshRequestId = Static<typeof SchemaRefreshRequestId>;

export const SchemaRefreshEntityDiffId = String.withBrand(
  "SchemaRefreshEntityDiffId",
);
export type SchemaRefreshEntityDiffId = Static<
  typeof SchemaRefreshEntityDiffId
>;

export const LanceTableActionId = String.withBrand("LanceTableActionId");
export type LanceTableActionId = Static<typeof LanceTableActionId>;

export const GridElementEntityId = Union(CellId, SharedFilterId);
export type GridElementEntityId = Static<typeof GridElementEntityId>;

export const NotificationUserLinkId = String.withBrand(
  "NotificationUserLinkId",
);
export type NotificationUserLinkId = Static<typeof NotificationUserLinkId>;

export const NotificationId = String.withBrand("NotificationId");
export type NotificationId = Static<typeof NotificationId>;

export const K8sResourceId = String.withBrand("K8sResourceId");
export type K8sResourceId = Static<typeof K8sResourceId>;

export const SemanticProjectId = String.withBrand("SemanticProjectId");
export type SemanticProjectId = Static<typeof SemanticProjectId>;

export const SemanticProjectVersionId = String.withBrand(
  "SemanticProjectVersionId",
);
export type SemanticProjectVersionId = Static<typeof SemanticProjectVersionId>;

export const SemanticProjectVersionBuildId = String.withBrand(
  "SemanticProjectVersionBuildId",
);
export type SemanticProjectVersionBuildId = Static<
  typeof SemanticProjectVersionBuildId
>;

export const SemanticProjectFileId = String.withBrand("SemanticProjectFileId");
export type SemanticProjectFileId = Static<typeof SemanticProjectFileId>;

export const SemanticProjectFilePath = String.withBrand(
  "SemanticProjectFilePath",
);
export type SemanticProjectFilePath = Static<typeof SemanticProjectFilePath>;

export const SemanticDatasetId = String.withBrand("SemanticDatasetId");
export type SemanticDatasetId = Static<typeof SemanticDatasetId>;

export const SemanticDatasetName = String.withBrand("SemanticDatasetName");
/**
 * Identifier for a semantic dataset name *or* a
 * user-joined table stored in `someExploreSpec.tables`
 * (since at computation-time these are treated like semantic datasets)
 */
export type SemanticDatasetName = Static<typeof SemanticDatasetName>;

/** Globally unique primary key for a persisted semantic view. Not stable across versions. */
export type SemanticViewId = Static<typeof SemanticViewId>;
export const SemanticViewId = String.withBrand("SemanticViewId");

/** Stable identifier for a semantic view across versions. Unique within a project version. */
export type SemanticViewName = Static<typeof SemanticViewName>;
export const SemanticViewName = String.withBrand("SemanticViewName");

export const SemanticObjectCategoryLinkId = String.withBrand(
  "SemanticObjectCategoryLinkId",
);
export type SemanticObjectCategoryLinkId = Static<
  typeof SemanticObjectCategoryLinkId
>;

export const SemanticDatasetStatusLinkId = String.withBrand(
  "SemanticDatasetStatusLinkId",
);
export type SemanticDatasetStatusLinkId = Static<
  typeof SemanticDatasetStatusLinkId
>;

export const SemanticViewStatusLinkId = String.withBrand(
  "SemanticViewStatusLinkId",
);
export type SemanticViewStatusLinkId = Static<typeof SemanticViewStatusLinkId>;

export const UserToGranteesId = String.withBrand("UserToGranteesId");
export type UserToGranteesId = Static<typeof UserToGranteesId>;

export const CollapsibleCellId = String.withBrand("CollapsibleCellId");
export type CollapsibleCellId = Static<typeof CollapsibleCellId>;

export const ComponentReferenceId = String.withBrand("ComponentReferenceId");
export type ComponentReferenceId = Static<typeof ComponentReferenceId>;

export const SemanticDatasetDataSourceTableLinkId = String.withBrand(
  "SemanticDatasetDataSourceTableLinkId",
);
export type SemanticDatasetDataSourceTableLinkId = Static<
  typeof SemanticDatasetDataSourceTableLinkId
>;

export const DocusignEnvelopeId = String.withBrand("DocusignEnvelopeId");
export type DocusignEnvelopeId = Static<typeof DocusignEnvelopeId>;

export const DocusignTemplateId = String.withBrand("DocusignTemplateId");
export type DocusignTemplateId = Static<typeof DocusignTemplateId>;

export const ReceivedDocusignEventId = String.withBrand(
  "ReceivedDocusignEventId",
);
export type ReceivedDocusignEventId = Static<typeof ReceivedDocusignEventId>;

// hygen-inject: cell-type-brand-id

export const BullQueueJobId = String.withBrand("BullQueueJobId");
export type BullQueueJobId = Static<typeof BullQueueJobId>;

export const TableReferenceId = String.withBrand("TableReferenceId");
export type TableReferenceId = Static<typeof TableReferenceId>;

export const TableEntryId = String.withBrand("TableEntryId");
export type TableEntryId = Static<typeof TableEntryId>;

export const ReceivedStripeEventId = String.withBrand("ReceivedStripeEventId");
export type ReceivedStripeEventId = Static<typeof ReceivedStripeEventId>;

export const ReceivedWorkOSEventId = String.withBrand("ReceivedWorkOSEventId");
export type ReceivedWorkOSEventId = Static<typeof ReceivedWorkOSEventId>;

export const QueryCacheEntryId = String.withBrand("QueryCacheEntryId");
export type QueryCacheEntryId = Static<typeof QueryCacheEntryId>;

export const HeartbeatId = String.withBrand("HeartbeatId");
export type HeartbeatId = Static<typeof HeartbeatId>;

export const TableDeletionQueueId = String.withBrand("TableDeletionQueueId");
export type TableDeletionQueueId = Static<typeof TableDeletionQueueId>;

export const DataSourceShardDeletionQueueEventType = Union(
  Literal("CATEGORY_DELETED"),
  Literal("DATA_CONNECTION_DELETED"),
  Literal("STATUS_DELETED"),
);
export type DataSourceShardDeletionQueueEventType = Static<
  typeof DataSourceShardDeletionQueueEventType
>;

export const DataSourceShardDeletionQueueId = String.withBrand(
  "DataSourceShardDeletionQueueId",
);
export type DataSourceShardDeletionQueueId = Static<
  typeof DataSourceShardDeletionQueueId
>;

export const FileDeletionQueueId = String.withBrand("FileDeletionQueueId");
export type FileDeletionQueueId = Static<typeof FileDeletionQueueId>;

export const OutputContentDeletionQueueId = String.withBrand(
  "OutputContentDeletionQueueId",
);
export type OutputContentDeletionQueueId = Static<
  typeof OutputContentDeletionQueueId
>;

export const AgentChatThreadId = String.withBrand("AgentChatThreadId");
export type AgentChatThreadId = Static<typeof AgentChatThreadId>;

export const AgentCellEditId = String.withBrand("AgentCellEditId");
export type AgentCellEditId = Static<typeof AgentCellEditId>;

export const AgentSemanticProjectFileEditId = String.withBrand(
  "AgentSemanticProjectFileEditId",
);
export type AgentSemanticProjectFileEditId = Static<
  typeof AgentSemanticProjectFileEditId
>;

export const AgentChatMessageId = String.withBrand("AgentChatMessageId");
export type AgentChatMessageId = Static<typeof AgentChatMessageId>;

export const AgentChatQueuedPromptId = String.withBrand(
  "AgentChatQueuedPromptId",
);
export type AgentChatQueuedPromptId = Static<typeof AgentChatQueuedPromptId>;

export const StaticAgentChatMessageId = String.withBrand(
  "StaticAgentChatMessageId",
);
export type StaticAgentChatMessageId = Static<typeof StaticAgentChatMessageId>;

export const AgentChatRunId = String.withBrand("AgentChatRunId");
export type AgentChatRunId = Static<typeof AgentChatRunId>;

export const AgentFeedbackId = String.withBrand("AgentFeedbackId");
export type AgentFeedbackId = Static<typeof AgentFeedbackId>;

export const ThreadExtractionId = String.withBrand("ThreadExtractionId");
export type ThreadExtractionId = Static<typeof ThreadExtractionId>;

export const ThreadSummaryId = String.withBrand("ThreadSummaryId");
export type ThreadSummaryId = Static<typeof ThreadSummaryId>;

export const ThreadConfusionId = String.withBrand("ThreadConfusionId");
export type ThreadConfusionId = Static<typeof ThreadConfusionId>;

export const ThreadTopicId = String.withBrand("ThreadTopicId");
export type ThreadTopicId = Static<typeof ThreadTopicId>;

export const ThreadTopicAssignmentId = String.withBrand(
  "ThreadTopicAssignmentId",
);
export type ThreadTopicAssignmentId = Static<typeof ThreadTopicAssignmentId>;

export const BillingCycleAiBillingUsageAggregationId = String.withBrand(
  "BillingCycleAiBillingUsageAggregationId",
);
export type BillingCycleAiBillingUsageAggregationId = Static<
  typeof BillingCycleAiBillingUsageAggregationId
>;

export const BillingCycleCreditGrantConsumptionAggregationId = String.withBrand(
  "BillingCycleCreditGrantConsumptionAggregationId",
);
export type BillingCycleCreditGrantConsumptionAggregationId = Static<
  typeof BillingCycleCreditGrantConsumptionAggregationId
>;

export const AiBillingUsageId = String.withBrand("AiBillingUsageId");
export type AiBillingUsageId = Static<typeof AiBillingUsageId>;

export const CreditGrantId = String.withBrand("CreditGrantId");
export type CreditGrantId = Static<typeof CreditGrantId>;

export const CreditGrantConsumptionId = String.withBrand(
  "CreditGrantConsumptionId",
);
export type CreditGrantConsumptionId = Static<typeof CreditGrantConsumptionId>;

export const CreditGrantConsumptionLogId = String.withBrand(
  "CreditGrantConsumptionLogId",
);
export type CreditGrantConsumptionLogId = Static<
  typeof CreditGrantConsumptionLogId
>;

export const AggregatedAiBillingUsageId = String.withBrand(
  "AggregatedAiBillingUsageId",
);
export type AggregatedAiBillingUsageId = Static<
  typeof AggregatedAiBillingUsageId
>;

export const AggregatedUserAiBillingUsageId = String.withBrand(
  "AggregatedUserAiBillingUsageId",
);
export type AggregatedUserAiBillingUsageId = Static<
  typeof AggregatedUserAiBillingUsageId
>;

export const UserToHubspotContactId = String.withBrand(
  "UserToHubspotContactId",
);
export type UserToHubspotContactId = Static<typeof UserToHubspotContactId>;

export const HubspotContactId = String.withBrand("HubspotContactId");
export type HubspotContactId = Static<typeof HubspotContactId>;

export const HubspotAnonymousId = String.withBrand("HubspotAnonymousId");
export type HubspotAnonymousId = Static<typeof HubspotAnonymousId>;

export const ContextSuggestionSourceId = String.withBrand(
  "ContextSuggestionSourceId",
);
export type ContextSuggestionSourceId = Static<
  typeof ContextSuggestionSourceId
>;

export const ContextSuggestionIssueId = String.withBrand(
  "ContextSuggestionIssueId",
);
export type ContextSuggestionIssueId = Static<typeof ContextSuggestionIssueId>;

export const ContextSuggestionRunId = String.withBrand(
  "ContextSuggestionRunId",
);
export type ContextSuggestionRunId = Static<typeof ContextSuggestionRunId>;

export const ContextSuggestionFileUpdateId = String.withBrand(
  "ContextSuggestionFileUpdateId",
);
export type ContextSuggestionFileUpdateId = Static<
  typeof ContextSuggestionFileUpdateId
>;

export const ContextSuggestionDataWarehouseUpdateId = String.withBrand(
  "ContextSuggestionDataWarehouseUpdateId",
);
export type ContextSuggestionDataWarehouseUpdateId = Static<
  typeof ContextSuggestionDataWarehouseUpdateId
>;

export const ContextChangesetId = String.withBrand("ContextChangesetId");
export type ContextChangesetId = Static<typeof ContextChangesetId>;

export const ContextVersionId = String.withBrand("ContextVersionId");
export type ContextVersionId = Static<typeof ContextVersionId>;

/**
 * Represents any possible database entity id in our codebase
 */
export type DataBaseEntityId =
  | AppSessionAOLogId
  | AppSessionCellId
  | AppSessionEventLogId
  | AppSessionFileId
  | AppSessionGrantId
  | AppSessionId
  | AppSessionDeletionEventId
  | AiBillingUsageId
  | AggregatedAiBillingUsageId
  | AggregatedUserAiBillingUsageId
  | ApiClientId
  | AgentChatThreadId
  | AgentCellEditId
  | AgentSemanticProjectFileEditId
  | AgentChatMessageId
  | AgentChatQueuedPromptId
  | AgentFeedbackId
  | BigQueryOAuthConnectionId
  | BlockCellId
  | CanvasElementId
  | CanvasLayoutId
  | DataAppElementId
  | CategoryId
  | CellGroupCellId
  | CellId
  | ChartCellId
  | ChartCellSessionStateId
  | CodeCellId
  | CollectionGrantId
  | CollectionHexLinkId
  | CollectionId
  | ColumnPropertiesId
  | ColorPaletteId
  | CompactedAppSessionEventLogId
  | ComputeHeartbeatId
  | CustomCodeFormattingConfigId
  | CustomSuggestionsConfigId
  | CommentId
  | CommentReactionId
  | ComponentImportCellId
  | ComputeActionId
  | CreditGrantId
  | DatabricksOAuthConnectionId
  | DataConnectionSchemaRefreshId
  | DataConnectionSchemaRefreshScheduleId
  | DataConnectionHexVersionLinkId
  | DataConnectionId
  | DataConnectionOAuthSettingsId
  | DataSourceColumnId
  | DataSourceColumnMetadataId
  | DataSourceDatabaseId
  | DataSourceDatabaseMetadataId
  | DataSourceDbtMetricId
  | DataSourceGrantId
  | DataSourceSchemaId
  | DataSourceSchemaMetadataId
  | DbtMetricCellDataSourceDbtMetricLinkId
  | DbtMetricCellId
  | DeviceCodeId
  | DataSourceTableId
  | DataSourceTableMetadataId
  | DataSourceObjectCategoryLinkId
  | DisplayTableCellId
  | DisplayTableConfigId
  | DisplayTableSessionStateId
  | DomainDenialId
  | EmailDenialId
  | EvalBaseId
  | EvalResultId
  | EvalRubricId
  | EvalRunId
  | EvalSetId
  | EvalSetRunId
  | EvalSemanticProjectFileId
  | ExploreCellId
  | ExploreSpecSessionStateId
  | ExternalContextConnectionId
  | K8sResourceId
  | EmbeddedApiCallId
  | EmbeddedOAuthCredentialsId
  | EnvVarId
  | ExecutionEventId
  | ExternalFileIntegrationId
  | ExternalFileIntegrationHexVersionLinkId
  | ExploreAgentSettingsId
  | FileHexVersionLinkId
  | FileId
  | FilterCellId
  | FilterCellSessionStateId
  | GithubServerConfigId
  | GoogleDriveOauthAccountId
  | GridElementId
  | GridColumnId
  | GridLayoutId
  | GridRowId
  | GroupId
  | HexAnalyticsId
  | SystemMagicEventId
  | MagicRulesFileId
  | HexId
  | HexAOLogId
  | HexCategoryLinkId
  | HexRetrievalEventId
  | HexVersionOAuthSettingsId
  | HexVersionDefaultAppSessionId
  | HexScheduleId
  | HexVersionAOLogId
  | HexVersionId
  | HexVersionSecretId
  | DockerImageRepositoryId
  | DockerImageTagId
  | KernelSourceImageId
  | KernelSourceImageVersionId
  | KernelSourceImageVersionId
  | LanceTableActionId
  | LanceTableOfDataSourceColumnsId
  | LanceTableOfHexesId
  | LanceTableReferenceId
  | LanceTableReferenceConstraintId
  | LlmRequestLogId
  | LinkId
  | MagicApiKeyId
  | MagicCellContentsId
  | MagicCompletionContextId
  | MagicCompletionEventId
  | MagicEventId
  | MapCellId
  | MarkdownCellId
  | MetricCellId
  | McpAppSwapTokenId
  | NotificationPreferencesId
  | NotionBotId
  | NotionPreviewId
  | ThreadExtractionId
  | ThreadSummaryId
  | ThreadConfusionId
  | ThreadTopicId
  | ThreadTopicAssignmentId
  | ThreadExtractionEvalResultId
  | ThreadExtractionExperimentId
  | OrgAppIconId
  | OrgCustomFontId
  | OrgDomainId
  | OrgDomainVerificationLinkId
  | OrgId
  | OrgPublishedAppThemeId
  | OrgGuideFileId
  | OrgGuideFileVersionId
  | OrgGuideFileVersionMentionId
  | OrgGuideFilesAOLogId
  | OutputContentId
  | OutputId
  | ParameterId
  | ParameterValueId
  | PinnedSchemaObjectId
  | PivotCellId
  | PresenceId
  | PresignedEmbeddingUrlId
  | PaidComputeGrantId
  | ProjectGrantId
  | ProjectViewId
  | ProjectStateId
  | ProjectRunMetadataId
  | PromoId
  | ReviewId
  | ReviewRequestId
  | ReviewRequestLinkId
  | ReviewRequestAOLogId
  | ScheduleNotificationRecipientId
  | ScopeItemId
  | SemanticRetrievalEventId
  | SecretId
  | SemanticProjectVersionAOLogId
  | SharedFilterId
  | SharedFilterSessionStateId
  | SharedAssetGrantId
  | SlackAgentChatThreadLinkId
  | SlackAuthorizationId
  | SnowflakeOAuthConnectionId
  | SnowflakeOAuthRolePreferenceId
  | SqlCellId
  | SshKeyId
  | StatusId
  | StoryElementId
  | StoryLayoutId
  | StripePriceId
  | StripeProductId
  | StripeSubscriptionId
  | StripeSubscriptionItemId
  | SyncRepositoryHexVersionLinkId
  | SyncRepositoryHexVersionLinkId
  | SyncRepositoryId
  | TaskId
  | TaskConfigId
  | TaskRecipientId
  | TextCellId
  | UILockId
  | UserGroupLinkId
  | UserId
  | UserBigQueryOAuthConnectionId
  | UserDatabricksOAuthConnectionId
  | UserSnowflakeOAuthConnectionId
  | UserStarredHexLinkId
  | UserStarredCollectionLinkId
  | UserMemoryId
  | UserMemoryVersionId
  | UserMemoryExtractionRunId
  | VcsConnectionId
  | VcsPackageId
  | VcsPackageHexVersionLinkId
  | VcsPackageHexVersionLinkId
  | VegaChartCellId
  | WritebackCellId
  | ScheduleNotificationId
  | ScheduleNotificationExportId
  | ScheduleNotificationConditionId
  | SlackChannelId
  | SlackTeamAuthorizationId
  | SlackUserId
  | RunNotificationRecipientId
  | ExploreId
  | ExploreSessionStateId
  | TableQueryEventId
  | TableCoocQueryEventId
  | NotificationUserLinkId
  | NotificationId
  | SqlConfigId
  | CollapsibleCellId
  // hygen-inject: cell-super-id
  | SchemaRefreshEntityDiffId
  | SemanticProjectId
  | SemanticProjectVersionId
  | SemanticProjectVersionBuildId
  | SemanticProjectFileId
  | SemanticDatasetId
  | SemanticViewId
  | SemanticObjectCategoryLinkId
  | SemanticDatasetStatusLinkId
  | SemanticViewStatusLinkId
  | UserToGranteesId
  | SavedViewId
  | SavedViewHexScheduleLinkId
  | SavedViewScheduleNotificationLinkId
  | SemanticDatasetDataSourceTableLinkId
  | ContextSuggestionSourceId
  | ContextSuggestionIssueId
  | ContextSuggestionRunId
  | ContextSuggestionFileUpdateId
  | ContextSuggestionDataWarehouseUpdateId
  | ContextChangesetId
  | ContextVersionId
  | TableReferenceId
  | TableEntryId
  | DocusignEnvelopeId
  | ReceivedDocusignEventId
  | ReceivedStripeEventId
  | ReceivedWorkOSEventId
  | QueryCacheEntryId
  | HeartbeatId
  | TableDeletionQueueId
  | DataSourceShardDeletionQueueId
  | FileDeletionQueueId
  | OutputContentDeletionQueueId
  | UserToHubspotContactId
  | McpServerConfigurationId
  | UserMcpServerConnectionId
  | BillingCycleAiBillingUsageAggregationId
  | BillingCycleCreditGrantConsumptionAggregationId
  | CreditGrantConsumptionLogId
  | CreditGrantConsumptionId;

/**
 * Represents any possible entity or entity like id in our codebase
 */
export type SuperId =
  | DataBaseEntityId
  | IntrinsicGroupId
  | MapLayerId
  | KernelId
  | VcsAccessTokenId
  | PaymentMethodId
  | StaticDataConnectionId
  | StaticCellId
  | ParentDataConnectionId
  | FilterCellColumnId
  | ESDocId
  | KernelImage
  | PackageId
  | SearchHexesResultId
  | StripeCustomerId
  | DisplayTableColumnId;
